import { useCallback, useEffect, useRef, useState } from 'react'

const ARROW_SIZE = 10
const MIN_THUMB_SIZE = 20
const SCROLL_STEP = 20

const getThumbMetrics = (clientSize, scrollSize, scrollOffset, arrowSize = ARROW_SIZE) => {
  const trackSize = Math.max(clientSize - arrowSize * 2, 0)

  if (clientSize <= 0 || trackSize <= 0) {
    return { trackSize, thumbSize: 0, thumbOffset: 0, canScroll: false }
  }

  if (scrollSize <= clientSize) {
    return { trackSize, thumbSize: trackSize, thumbOffset: 0, canScroll: false }
  }

  const thumbSize = Math.max(MIN_THUMB_SIZE, Math.round((clientSize / scrollSize) * trackSize))
  const maxThumbOffset = Math.max(trackSize - thumbSize, 0)
  const scrollRange = scrollSize - clientSize
  const thumbOffset = scrollRange > 0 ? (scrollOffset / scrollRange) * maxThumbOffset : 0

  return { trackSize, thumbSize, thumbOffset, canScroll: true }
}

const MayaOutlinerScrollArea = ({ children, className = '' }) => {
  const viewportRef = useRef(null)
  const [metrics, setMetrics] = useState({
    scrollTop: 0,
    scrollLeft: 0,
    scrollHeight: 0,
    clientHeight: 0,
    scrollWidth: 0,
    clientWidth: 0,
  })

  const updateMetrics = useCallback(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    setMetrics({
      scrollTop: viewport.scrollTop,
      scrollLeft: viewport.scrollLeft,
      scrollHeight: viewport.scrollHeight,
      clientHeight: viewport.clientHeight,
      scrollWidth: viewport.scrollWidth,
      clientWidth: viewport.clientWidth,
    })
  }, [])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return undefined

    updateMetrics()

    const resizeObserver = new ResizeObserver(updateMetrics)
    resizeObserver.observe(viewport)

    const content = viewport.firstElementChild
    if (content) resizeObserver.observe(content)

    viewport.addEventListener('scroll', updateMetrics, { passive: true })
    window.addEventListener('resize', updateMetrics)

    return () => {
      resizeObserver.disconnect()
      viewport.removeEventListener('scroll', updateMetrics)
      window.removeEventListener('resize', updateMetrics)
    }
  }, [updateMetrics, children])

  const scrollBy = (deltaX, deltaY) => {
    const viewport = viewportRef.current
    if (!viewport) return
    viewport.scrollBy({ left: deltaX, top: deltaY, behavior: 'auto' })
  }

  const vertical = getThumbMetrics(metrics.clientHeight, metrics.scrollHeight, metrics.scrollTop)
  const horizontal = getThumbMetrics(metrics.clientWidth, metrics.scrollWidth, metrics.scrollLeft)

  const handleVerticalTrackClick = (event) => {
    const viewport = viewportRef.current
    if (!viewport || !vertical.canScroll) return

    const track = event.currentTarget.getBoundingClientRect()
    const clickOffset = event.clientY - track.top
    const targetThumbOffset = clickOffset - vertical.thumbSize / 2
    const maxThumbOffset = Math.max(vertical.trackSize - vertical.thumbSize, 0)
    const clampedThumbOffset = Math.min(Math.max(targetThumbOffset, 0), maxThumbOffset)
    const scrollRatio = maxThumbOffset > 0 ? clampedThumbOffset / maxThumbOffset : 0

    viewport.scrollTop = scrollRatio * (metrics.scrollHeight - metrics.clientHeight)
  }

  const handleHorizontalTrackClick = (event) => {
    const viewport = viewportRef.current
    if (!viewport || !horizontal.canScroll) return

    const track = event.currentTarget.getBoundingClientRect()
    const clickOffset = event.clientX - track.left
    const targetThumbOffset = clickOffset - horizontal.thumbSize / 2
    const maxThumbOffset = Math.max(horizontal.trackSize - horizontal.thumbSize, 0)
    const clampedThumbOffset = Math.min(Math.max(targetThumbOffset, 0), maxThumbOffset)
    const scrollRatio = maxThumbOffset > 0 ? clampedThumbOffset / maxThumbOffset : 0

    viewport.scrollLeft = scrollRatio * (metrics.scrollWidth - metrics.clientWidth)
  }

  return (
    <div className={['maya-outliner-scrollport', className].filter(Boolean).join(' ')}>
      <div ref={viewportRef} className="maya-outliner-tree">
        {children}
      </div>

      <div className="maya-outliner-scrollbar-v" aria-hidden="true">
        <button
          type="button"
          tabIndex={-1}
          className="maya-outliner-scroll-arrow is-up"
          aria-hidden="true"
          onClick={() => scrollBy(0, -SCROLL_STEP)}
        />
        <div className="maya-outliner-scroll-track is-vertical" onClick={handleVerticalTrackClick}>
          <div
            className="maya-outliner-scroll-thumb is-vertical"
            style={{
              height: `${vertical.thumbSize}px`,
              transform: `translateY(${vertical.thumbOffset}px)`,
            }}
          />
        </div>
        <button
          type="button"
          tabIndex={-1}
          className="maya-outliner-scroll-arrow is-down"
          aria-hidden="true"
          onClick={() => scrollBy(0, SCROLL_STEP)}
        />
      </div>

      <div className="maya-outliner-scrollbar-h" aria-hidden="true">
        <button
          type="button"
          tabIndex={-1}
          className="maya-outliner-scroll-arrow is-left"
          aria-hidden="true"
          onClick={() => scrollBy(-SCROLL_STEP, 0)}
        />
        <div className="maya-outliner-scroll-track is-horizontal" onClick={handleHorizontalTrackClick}>
          <div
            className="maya-outliner-scroll-thumb is-horizontal"
            style={{
              width: `${horizontal.thumbSize}px`,
              transform: `translateX(${horizontal.thumbOffset}px)`,
            }}
          />
        </div>
        <button
          type="button"
          tabIndex={-1}
          className="maya-outliner-scroll-arrow is-right"
          aria-hidden="true"
          onClick={() => scrollBy(SCROLL_STEP, 0)}
        />
      </div>

      <div className="maya-outliner-scroll-corner" aria-hidden="true" />
    </div>
  )
}

export default MayaOutlinerScrollArea
